'use client';

import { routes, Routes } from '@cntxt/domains/access-control';

const LOGIN_PATH = routes.LOGIN.url;

function isUnderLoginPath(pathname: string): boolean {
  return pathname === LOGIN_PATH || pathname.startsWith(`${LOGIN_PATH}/`);
}

/**
 * Middleware (`accessDenied`) sends users here with `requestedPath` when
 * redirecting from a protected route.
 *
 * @param origin - Current window origin.
 * @returns A same-origin, non-login absolute URL, or `null` when the param is
 *   absent / unsafe.
 */
function getSafeRedirectFromRequestedPath(origin: string): string | null {
  const raw = new URLSearchParams(window.location.search).get('requestedPath');
  if (!raw || !raw.startsWith('/') || raw.startsWith('//')) {
    return null;
  }

  try {
    const resolved = new URL(raw, origin);
    if (resolved.origin !== origin) {
      return null;
    }
    if (isUnderLoginPath(resolved.pathname)) {
      return null;
    }
    return resolved.href;
  } catch {
    return null;
  }
}

/**
 * Returns the safe post-OAuth redirect target URL.
 *
 * Using `location.href` directly when starting sign-in from the custom sign-in
 * page sets `callbackUrl` to `/auth/signIn`, which makes Auth.js / NextAuth
 * nest `callbackUrl` on each retry and causes an infinite redirect chain.
 *
 * Resolution order:
 * 1. The decoded `callbackUrl` query param (same-origin, non-login paths only).
 * 2. The `requestedPath` query param set by the `accessDenied` middleware.
 * 3. Home page URL as fallback.
 *
 * @returns Absolute same-origin URL safe to pass as `redirectTo` to `signIn()`.
 */
export function getOAuthRedirectTargetUrl(): string {
  if (typeof window === 'undefined') {
    return routes[Routes.HOME].url;
  }

  const origin = window.location.origin;
  const fallback = new URL(routes[Routes.HOME].url, origin).href;

  const rawCallback = new URLSearchParams(window.location.search).get(
    'callbackUrl',
  );
  if (!rawCallback) {
    return getSafeRedirectFromRequestedPath(origin) ?? fallback;
  }

  let candidate = rawCallback;
  for (let i = 0; i < 5; i += 1) {
    try {
      const next = decodeURIComponent(candidate);
      if (next === candidate) {
        break;
      }
      candidate = next;
    } catch {
      return getSafeRedirectFromRequestedPath(origin) ?? fallback;
    }
  }

  try {
    const resolved = new URL(candidate, origin);
    if (resolved.origin !== origin) {
      return getSafeRedirectFromRequestedPath(origin) ?? fallback;
    }
    if (isUnderLoginPath(resolved.pathname)) {
      return getSafeRedirectFromRequestedPath(origin) ?? fallback;
    }
    return resolved.href;
  } catch {
    return getSafeRedirectFromRequestedPath(origin) ?? fallback;
  }
}
